import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import qs from "qs";
import { TinyApiTransport } from "./transport";
import GeneratedApi, { ContentType, FullRequestParams } from "./generated";

function preparePath(path: string): string {
  if (path.startsWith("/")) {
    const controller = path.substring(1).split("/")[0];
    switch (controller) {
      case "segment":
      case "audience":
      case "customer":
      case "customer-event":
      case "customer-action":
      case "suppression":
      case "pre-defined-segment":
      case "ai-traffic-control":
      case "pre-signed-url":
      case "custom-attributes":
      case "global-active-engager":
      case "engagement-scoring":
        return `/customerservice${path}`;
      case "tone":
      case "merge-tag":
      case "content":
        return `/einsteinservice${path}`;
      case "form":
      case "form-template":
      case "forms":
        return `/formservice${path}`;
      case "automation":
        return `/automationservice${path}`;
      case "analytics":
      case "dashboard":
        return `/reportservice${path}`;
      case "report":
      case "dkim":
      case "relay-management":
        return `/notificationservice${path}`;
      case "email-auth":
      case "entri":
        return `/notificationservice/v0${path}`;
      case "account-member":
      case "api-keys":
        return `/authorization-server${path}`;
      case "workflow":
      case "workflow-email-template":
        return `/workflowservice${path}`;
      case "html-template":
      case "campaign":
      case "account-html-template":
      case "campaign-layout":
      case "template-tag":
      case "campaign-analytics":
        return `/campaignservice${path}`;
      case "product":
      case "credits":
        return `/billingservice${path}`;
      case "sms":
        // Statistics exports are served by tiny-svc-reports.
        if (path.includes("statistics/export")) {
          return `/reportservice${path}`;
        }
        return `/sms-service${path}`;
      default:
        return `/clientservice${path}`;
    }
  }
  return path;
}

export class TinyApiClient extends GeneratedApi {
  /** Public so hosts can attach extra interceptors (e.g. auth-refresh). */
  public readonly instance: AxiosInstance;
  /** Host-provided backend base URL (useful for building export/download links). */
  public readonly baseUrl: string;

  constructor(transport: TinyApiTransport) {
    super();
    this.baseUrl = transport.baseUrl;
    this.instance = axios.create({ baseURL: transport.baseUrl });
    this.instance.interceptors.request.use((config: any) => {
      if (
        config.url &&
        transport.isAuthExempt?.(config.url, config.method ?? "get")
      ) {
        return config;
      }
      const token = transport.getToken();
      if (config.headers) {
        config.headers.Authorization = token ? `Bearer ${token}` : "";
      }
      return config;
    });
    this.instance.interceptors.response.use(undefined, (error: unknown) => {
      transport.onResponseError?.(error);
      return Promise.reject(error);
    });
  }

  async request<T = any>({
    path,
    method,
    body,
    type,
    query,
    format,
    headers,
    baseURL,
  }: FullRequestParams): Promise<AxiosResponse<T>> {
    const options: AxiosRequestConfig = {
      url: preparePath(path),
      method,
      responseType: format,
      headers,
      paramsSerializer: function (params: any) {
        return qs.stringify(params, { indices: false, encode: false });
      },
    };
    if (type === ContentType.FormData && body && typeof body === "object") {
      const formData = new FormData();
      for (const key in body) {
        const item: any = body[key as keyof typeof body];
        if (item instanceof File) {
          formData.append(key, item);
        } else {
          const blob = new Blob([JSON.stringify(item)], {
            type: "application/json",
          });
          formData.append(key, blob);
        }
      }
      options.data = formData;
    } else {
      if (body) {
        options.data = body;
      }
      if (query) {
        options.params = query;
      }
    }
    if (baseURL) {
      options.baseURL = baseURL;
    }
    return this.instance.request(options);
  }
}
